import { createSlice } from "@reduxjs/toolkit";
import { createSelector } from "reselect";

const mapHoliday = (h) => ({
  id: h.Id,
  appUserId: h.AppUserId,
  startDateTime: h.UtcStartDateTime,
  endDateTime: h.UtcEndDateTime,
  durationType: h.DurationType,
  partDayType: h.PartDayType,
  comments: h.Comments,
  status: h.Status || "Approved",
  requestedDateTime: h.UtcRequestedDateTime || null,
  actionedUserId: h.ActionedUserId || null,
  actionedDateTime: h.UtcActionedDateTime || null,
});

const mapSickness = (s) => ({
  id: s.Id,
  appUserId: s.AppUserId,
  startDateTime: s.UtcStartDateTime,
  endDateTime: s.UtcEndDateTime,
});

const mapOther = (o) => ({
  id: o.Id,
  appUserId: o.AppUserId,
  startDateTime: o.UtcStartDateTime,
  endDateTime: o.UtcEndDateTime,
  eventType: o.EventType,
  durationMins: o.DurationMins,
  comments: o.Comments,
});

const mapPublicHoliday = (p) => ({
  id: p.Id,
  title: p.Title,
  startDateTime: p.UtcStartDateTime,
  endDateTime: p.UtcEndDateTime,
});

const slice = createSlice({
  name: "hrLeave",
  initialState: {
    holidays: [],
    sicknesses: [],
    others: [],
    publicHolidays: [],
    viewMode: "calendar",
    refreshNonce: 0,
    requestsRefreshNonce: 0,
  },
  reducers: {
    hrLeaveViewModeUpdated: (state, action) => {
      state.viewMode = action.payload || "calendar";
    },
    hrLeaveRefreshRequested: (state) => {
      state.refreshNonce = (state.refreshNonce || 0) + 1;
    },
    hrLeaveRequestsRefreshRequested: (state) => {
      state.requestsRefreshNonce = (state.requestsRefreshNonce || 0) + 1;
    },
    hrLeaveHolidaysInitialised: (state, action) => {
      state.holidays = (action.payload.data || []).map(mapHoliday);
    },
    hrLeaveHolidayAdded: (state, action) => {
      const h = action.payload.data;
      if (!h || h.Id === undefined) return;
      state.holidays.push(mapHoliday(h));
    },
    hrLeaveHolidayUpdated: (state, action) => {
      const h = action.payload.data;
      if (!h || h.Id === undefined) return;
      const index = state.holidays.findIndex((item) => item.id === h.Id);
      if (index < 0) return;
      state.holidays[index] = mapHoliday(h);
    },
    hrLeaveHolidayRemoved: (state, action) => {
      const id = action.payload.id;
      state.holidays = state.holidays.filter((h) => h.id !== id);
    },
    hrLeaveSicknessesInitialised: (state, action) => {
      state.sicknesses = (action.payload.data || []).map(mapSickness);
    },
    hrLeaveSicknessAdded: (state, action) => {
      const s = action.payload.data;
      if (!s || s.Id === undefined) return;
      state.sicknesses.push(mapSickness(s));
    },
    hrLeaveSicknessUpdated: (state, action) => {
      const s = action.payload.data;
      if (!s || s.Id === undefined) return;
      const index = state.sicknesses.findIndex((item) => item.id === s.Id);
      if (index < 0) return;
      state.sicknesses[index] = mapSickness(s);
    },
    hrLeaveSicknessRemoved: (state, action) => {
      const id = action.payload.id;
      state.sicknesses = state.sicknesses.filter((s) => s.id !== id);
    },
    hrLeaveOthersInitialised: (state, action) => {
      state.others = (action.payload.data || []).map(mapOther);
    },
    hrLeaveOtherAdded: (state, action) => {
      const o = action.payload.data;
      if (!o || o.Id === undefined) return;
      state.others.push(mapOther(o));
    },
    hrLeaveOtherUpdated: (state, action) => {
      const o = action.payload.data;
      if (!o || o.Id === undefined) return;
      const index = state.others.findIndex((item) => item.id === o.Id);
      if (index < 0) return;
      state.others[index] = mapOther(o);
    },
    hrLeaveOtherRemoved: (state, action) => {
      const id = action.payload.id;
      state.others = state.others.filter((o) => o.id !== id);
    },
    hrLeavePublicHolidaysInitialised: (state, action) => {
      state.publicHolidays = (action.payload.data || []).map(mapPublicHoliday);
    },
    hrLeavePublicHolidayAdded: (state, action) => {
      const p = action.payload.data;
      if (!p || p.Id === undefined) return;
      state.publicHolidays.push(mapPublicHoliday(p));
    },
    hrLeavePublicHolidayUpdated: (state, action) => {
      const p = action.payload.data;
      if (!p || p.Id === undefined) return;
      const index = state.publicHolidays.findIndex((item) => item.id === p.Id);
      if (index < 0) return;
      state.publicHolidays[index] = mapPublicHoliday(p);
    },
    hrLeavePublicHolidayRemoved: (state, action) => {
      const id = action.payload.id;
      state.publicHolidays = state.publicHolidays.filter((p) => p.id !== id);
    },
  },
});

export const {
  hrLeaveViewModeUpdated,
  hrLeaveRefreshRequested,
  hrLeaveRequestsRefreshRequested,
  hrLeaveHolidaysInitialised,
  hrLeaveHolidayAdded,
  hrLeaveHolidayUpdated,
  hrLeaveHolidayRemoved,
  hrLeaveSicknessesInitialised,
  hrLeaveSicknessAdded,
  hrLeaveSicknessUpdated,
  hrLeaveSicknessRemoved,
  hrLeaveOthersInitialised,
  hrLeaveOtherAdded,
  hrLeaveOtherUpdated,
  hrLeaveOtherRemoved,
  hrLeavePublicHolidaysInitialised,
  hrLeavePublicHolidayAdded,
  hrLeavePublicHolidayUpdated,
  hrLeavePublicHolidayRemoved,
} = slice.actions;

export default slice.reducer;

export const getHrLeaveHolidays = createSelector(
  (state) => state.entities.hrLeave,
  (hrLeave) => hrLeave.holidays
);

export const getHrLeaveRefreshNonce = createSelector(
  (state) => state.entities.hrLeave,
  (hrLeave) => hrLeave.refreshNonce || 0
);

export const getHrLeaveRequestsRefreshNonce = createSelector(
  (state) => state.entities.hrLeave,
  (hrLeave) => hrLeave.requestsRefreshNonce || 0
);

export const getHrLeaveViewMode = createSelector(
  (state) => state.entities.hrLeave,
  (hrLeave) => hrLeave.viewMode || "calendar"
);

export const getHrLeaveSicknesses = createSelector(
  (state) => state.entities.hrLeave,
  (hrLeave) => hrLeave.sicknesses || []
);

export const getHrLeaveOthers = createSelector(
  (state) => state.entities.hrLeave,
  (hrLeave) => hrLeave.others || []
);

export const getHrLeavePublicHolidays = createSelector(
  (state) => state.entities.hrLeave,
  (hrLeave) => hrLeave.publicHolidays || []
);
